import React, { useEffect, useState } from 'react';
import { Layout, Menu, Typography } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  FileTextOutlined,
  SafetyOutlined,
  TeamOutlined,
  BellOutlined,
  SearchOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileOutlined,
  AuditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  CheckSquareOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { getProjects, getRatingAccess } from '../services/api';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface MainLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onNavigate: (page: string) => void;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, currentPage, onNavigate }) => {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [showQualityAudit, setShowQualityAudit] = useState(false);

  // Quality Audit is for audit projects (and for those who manage rating templates).
  useEffect(() => {
    Promise.all([getProjects(), getRatingAccess()])
      .then(([projects, access]) => {
        const hasAuditProject = projects.data.some((p: any) => p.engagement_mode === 'AUDIT');
        setShowQualityAudit(hasAuditProject || access.data.can_manage_templates);
      })
      .catch(() => setShowQualityAudit(false));
  }, []);

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'My Work',
    },
    {
      key: 'project-dashboard',
      icon: <ProjectOutlined />,
      label: 'Project Dashboard',
    },
    {
      key: 'ncr',
      icon: <SafetyOutlined />,
      label: 'NCR Register',
    },
    {
      key: 'dpr',
      icon: <FileTextOutlined />,
      label: 'DPR Register',
    },
    {
      key: 'mom',
      icon: <TeamOutlined />,
      label: 'MOM Register',
    },
    {
      key: 'site-instructions',
      icon: <FileTextOutlined />,
      label: 'Site Instructions',
    },
    {
      key: 'rfi',
      icon: <QuestionCircleOutlined />,
      label: 'RFI Register',
    },
    {
      key: 'mir',
      icon: <FileExcelOutlined />,
      label: 'MIR Register',
    },
    {
      key: 'wir',
      icon: <CheckSquareOutlined />,
      label: 'WIR Register',
    },
    {
      key: 'snag-register',
      icon: <WarningOutlined />,
      label: 'Snag Register',
    },
    ...(showQualityAudit
      ? [{ key: 'quality-audit', icon: <FileSearchOutlined />, label: 'Quality Audit' }]
      : []),
    {
      key: 'notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
    },
    {
      key: 'search',
      icon: <SearchOutlined />,
      label: 'Search',
    },
    {
        key: 'documents',
        icon: <FileOutlined />,
        label: 'Documents',
      },
    {
        key: 'audit-trail',
        icon: <AuditOutlined />,
        label: 'Audit Trail',
      },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
        key: 'admin',
        icon: <SettingOutlined />,
        label: 'Admin Config',
      },
    {
        key: 'excel-import',
        icon: <FileExcelOutlined />,
        label: 'Excel Import',
    },
    {
        key: 'client-dashboard',
        icon: <EyeOutlined />,
        label: 'Client View',
      },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        style={{
          background: '#1B2A4A',
        }}
        trigger={null}
      >
        {/* Logo */}
        <div style={{
          padding: collapsed ? '16px 8px' : '16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          marginBottom: 8,
        }}>
          {!collapsed ? (
            <div>
              <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 14, display: 'block' }}>
                QACA PMC
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                Governance Platform
              </Text>
            </div>
          ) : (
            <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>Q</Text>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[currentPage]}
          items={menuItems}
          onClick={({ key }) => onNavigate(key)}
          style={{ background: '#1B2A4A', borderRight: 'none' }}
        />
      </Sider>

      <Layout>
        <Header style={{
          background: 'white',
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {collapsed
              ? <MenuUnfoldOutlined onClick={() => setCollapsed(false)} style={{ fontSize: 18, cursor: 'pointer' }} />
              : <MenuFoldOutlined onClick={() => setCollapsed(true)} style={{ fontSize: 18, cursor: 'pointer' }} />
            }
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Text style={{ color: '#1B2A4A' }}>
              {user?.full_name}
            </Text>
            <Text
              style={{ color: '#2E75B6', cursor: 'pointer', fontSize: 13 }}
              onClick={logout}
            >
              Sign Out
            </Text>
          </div>
        </Header>

        <Content style={{
          margin: 0,
          minHeight: 280,
          background: '#f0f2f5',
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;